
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Customer, CustomerFilters } from "@/types/customer";
import { useLocalStorage } from "@/hooks/useLocalStorage";

export const useCustomers = (selectedRestaurants: string[]) => {
  // State management
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { toast } = useToast();
  
  // Store filters in localStorage for persistence
  const [filters, setFilters] = useLocalStorage<CustomerFilters>(
    "customer-filters",
    {
      registrationStatus: 'all',
      loyaltyTier: [],
      orderFrequency: 'all',
      lastOrderDays: [0, 365],
      spendRange: [0, 10000],
      marketingOptIn: null,
    }
  );
  
  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Helper function to get all restaurant IDs if needed
  const getAllRestaurantIds = async () => {
    const { data } = await supabase
      .from('restaurants_master')
      .select('restaurant_id');
    return data?.map(r => r.restaurant_id) || [];
  };

  // Main data fetch function
  const fetchCustomers = useCallback(async () => {
    try {
      console.log("Starting customer fetch with filters:", { 
        restaurants: selectedRestaurants,
        filters,
        page, 
        pageSize 
      });
      
      setLoading(true);
      setError(null);
      
      // Base query on customer_restaurant_activity with customers join
      let query = supabase
        .from('customer_restaurant_activity')
        .select(`
          *,
          customers!inner(
            id, email, first_name, last_name, is_registered, created_at
          )
        `);
      
      // Apply restaurant filter
      if (selectedRestaurants.length && !selectedRestaurants.includes('all')) {
        query = query.in('restaurant_id', selectedRestaurants);
      }
      
      // Apply registration status filter
      if (filters.registrationStatus !== 'all') {
        query = query.eq('customers.is_registered', filters.registrationStatus === 'registered');
      }
      
      // Apply marketing opt-in filter - now using restaurant-specific opt-in
      if (filters.marketingOptIn !== null) {
        query = query.eq('marketing_opt_in', filters.marketingOptIn);
      }
      
      // Apply loyalty tier filter
      if (filters.loyaltyTier.length > 0) {
        query = query.in('loyalty_tier', filters.loyaltyTier);
      }
      
      // Apply date range filters
      if (filters.lastOrderDays[0] > 0 || filters.lastOrderDays[1] < 365) {
        const now = new Date();
        
        if (filters.lastOrderDays[0] > 0) {
          const maxDate = new Date(now);
          maxDate.setDate(now.getDate() - filters.lastOrderDays[0]);
          query = query.lte('latest_order_date', maxDate.toISOString());
        }
        
        if (filters.lastOrderDays[1] < 365) {
          const minDate = new Date(now);
          minDate.setDate(now.getDate() - filters.lastOrderDays[1]);
          query = query.gte('latest_order_date', minDate.toISOString());
        }
      }
      
      // Apply spend range filters
      if (filters.spendRange[0] > 0) {
        query = query.gte('total_spend', filters.spendRange[0]);
      }
      
      if (filters.spendRange[1] < 10000) {
        query = query.lte('total_spend', filters.spendRange[1]);
      }
      
      // Apply search filter if present
      if (debouncedSearch) {
        query = query.or(`customers.first_name.ilike.%${debouncedSearch}%,customers.last_name.ilike.%${debouncedSearch}%,customers.email.ilike.%${debouncedSearch}%`);
      }

      // First get count for pagination
      const countQuery = supabase
        .from('customer_restaurant_activity')
        .select('customers!inner(id)', { count: 'exact', head: true });

      // Apply the same filters to count query
      if (selectedRestaurants.length && !selectedRestaurants.includes('all')) {
        countQuery.in('restaurant_id', selectedRestaurants);
      }
      
      if (filters.registrationStatus !== 'all') {
        countQuery.eq('customers.is_registered', filters.registrationStatus === 'registered');
      }
      
      if (filters.marketingOptIn !== null) {
        countQuery.eq('marketing_opt_in', filters.marketingOptIn);
      }
      
      if (filters.loyaltyTier.length > 0) {
        countQuery.in('loyalty_tier', filters.loyaltyTier);
      }
      
      if (filters.lastOrderDays[0] > 0 || filters.lastOrderDays[1] < 365) {
        const now = new Date();
        
        if (filters.lastOrderDays[0] > 0) {
          const maxDate = new Date(now);
          maxDate.setDate(now.getDate() - filters.lastOrderDays[0]);
          countQuery.lte('latest_order_date', maxDate.toISOString());
        }
        
        if (filters.lastOrderDays[1] < 365) {
          const minDate = new Date(now);
          minDate.setDate(now.getDate() - filters.lastOrderDays[1]);
          countQuery.gte('latest_order_date', minDate.toISOString());
        }
      }
      
      if (filters.spendRange[0] > 0) {
        countQuery.gte('total_spend', filters.spendRange[0]);
      }
      
      if (filters.spendRange[1] < 10000) {
        countQuery.lte('total_spend', filters.spendRange[1]);
      }
      
      if (debouncedSearch) {
        countQuery.or(`customers.first_name.ilike.%${debouncedSearch}%,customers.last_name.ilike.%${debouncedSearch}%,customers.email.ilike.%${debouncedSearch}%`);
      }
      
      // Execute count query
      const { count, error: countError } = await countQuery;
      
      if (countError) throw countError;
      
      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);
      
      // Execute query with sorting
      const { data, error: queryError } = await query
        .order('latest_order_date', { ascending: false, nullsFirst: false });
      
      if (queryError) throw queryError;
      
      console.log(`Successfully fetched ${data?.length || 0} customers (total: ${count || 0})`);
      
      // Process the data to group by customer
      const processedData = processRawCustomerData(data, filters);
      
      setCustomers(processedData);
      setFilteredCustomers(processedData);
      setTotalCount(count || 0);
      setTotalPages(Math.ceil((count || 0) / pageSize));
      
    } catch (err: any) {
      console.error('Error fetching customers:', err);
      setError(err.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load customer data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, selectedRestaurants, filters, page, pageSize, debouncedSearch]);

  // Process the raw data from the query
  const processRawCustomerData = (data: any[], filters: CustomerFilters) => {
    if (!data || !data.length) return [];
    
    // Group by customer ID
    const customerMap = new Map();
    
    data.forEach(item => {
      const customerId = item.customers.id;
      
      if (!customerMap.has(customerId)) {
        customerMap.set(customerId, {
          id: item.customers.id,
          email: item.customers.email,
          first_name: item.customers.first_name,
          last_name: item.customers.last_name,
          is_registered: item.customers.is_registered,
          marketing_opt_in: item.marketing_opt_in, // Now using restaurant-specific opt-in status
          created_at: item.customers.created_at,
          total_restaurants: 0,
          total_orders: 0,
          total_spend: 0,
          latest_order_date: null,
          restaurant_ids: [],
          loyalty_tiers: new Set()
        });
      }
      
      const customer = customerMap.get(customerId);
      
      // Add restaurant activity data
      customer.total_restaurants += 1;
      customer.total_orders += item.total_orders || 0;
      customer.total_spend += item.total_spend || 0;
      customer.restaurant_ids.push(item.restaurant_id);
      customer.loyalty_tiers.add(item.loyalty_tier);
      
      // Update latest order date if this one is more recent
      if (item.latest_order_date) {
        if (!customer.latest_order_date || new Date(item.latest_order_date) > new Date(customer.latest_order_date)) {
          customer.latest_order_date = item.latest_order_date;
        }
      }
    });
    
    // Convert to array and clean up
    let customers = Array.from(customerMap.values()).map(customer => ({
      ...customer,
      restaurant_ids: [...new Set(customer.restaurant_ids)],
      loyalty_tier: Array.from(customer.loyalty_tiers).sort()[0], // Just pick one tier for display
      loyalty_tiers: undefined // Remove the set
    }));
    
    // Apply order frequency filter client-side
    if (filters.orderFrequency !== 'all') {
      customers = customers.filter(customer => {
        const isFrequent = customer.total_orders > 10 && customer.total_restaurants > 2;
        const isOccasional = customer.total_orders > 1;
        
        if (filters.orderFrequency === 'frequent') return isFrequent;
        if (filters.orderFrequency === 'occasional') return !isFrequent && isOccasional;
        if (filters.orderFrequency === 'one_time') return customer.total_orders <= 1;
        
        return true;
      });
    }
    
    return customers;
  };
  
  // Effect to trigger the data fetch
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Function to manually refresh data
  const refreshData = () => {
    fetchCustomers();
  };
  
  // Function to update filters
  const updateFilters = (newFilters: CustomerFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      registrationStatus: 'all',
      loyaltyTier: [],
      orderFrequency: 'all',
      lastOrderDays: [0, 365],
      spendRange: [0, 10000],
      marketingOptIn: null,
    });
    setPage(1);
  };

  return {
    customers: filteredCustomers,
    filteredCustomers,
    searchQuery,
    setSearchQuery,
    loading,
    error,
    refreshData,
    filters,
    updateFilters,
    resetFilters,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
    totalCount
  };
};
